import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { trackAffiliateClick } from '../utils/metaPixel';
export function ProCreditWatchSection() {
  return <section className="py-24 lg:py-36 bg-offwhite relative" data-id="element-469">
      {/* Subtle dot grid background */}
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" data-id="element-470">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, #64748b 1px, transparent 1px)',
        backgroundSize: '24px 24px'
      }} data-id="element-471"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-472">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-473">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="inline-flex items-center px-3 py-1.5 rounded-full bg-navy/5 border border-navy/10 mb-6" data-id="element-474">
            <span className="text-navy text-xs font-bold tracking-widest uppercase" data-id="element-475">
              Monitoring Systems Reviewed
            </span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold font-serif text-navy mb-6 tracking-tight" data-id="element-476">
            Monitor bureau activity while managing disputes with documented
            visibility
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-lg text-slate leading-[1.75] max-w-[65ch] mx-auto" data-id="element-477">
            Built for consumers who want daily monitoring, organized
            documentation, and transparent reporting workflows.
          </motion.p>
        </div>

        {/* Three Pillars */}
        <div className="grid md:grid-cols-3 gap-8 mb-20" data-id="element-478">
          {/* Monitor Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} whileHover={{
          y: -8,
          boxShadow: '0 0 30px rgba(31, 169, 113, 0.15)'
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="bg-white p-10 rounded-3xl border border-slate-100 shadow-card relative overflow-hidden group" data-id="element-479">
            <div className="absolute top-0 right-0 w-48 h-48 bg-radial-gradient from-emerald-50 to-transparent opacity-50 pointer-events-none transition-opacity group-hover:opacity-100" data-id="element-480"></div>

            <div className="relative z-10" data-id="element-481">
              <div className="w-16 h-16 mb-8" data-id="element-482">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-483">
                  <path d="M12 40L32 52L52 40V24L32 12L12 24V40Z" fill="#1FA971" fillOpacity="0.1" data-id="element-484" />
                  <path d="M32 52L52 40V24L32 36V52Z" fill="#1FA971" fillOpacity="0.2" data-id="element-485" />
                  <path d="M12 24L32 36L52 24L32 12L12 24Z" fill="#1FA971" fillOpacity="0.3" data-id="element-486" />
                  <path d="M12 40L32 52L52 40V24L32 12L12 24V40Z" stroke="#0F2747" strokeWidth="2" strokeLinejoin="round" data-id="element-487" />
                  <path d="M32 52V36" stroke="#0F2747" strokeWidth="2" strokeLinejoin="round" data-id="element-488" />
                  <path d="M12 24L32 36L52 24" stroke="#0F2747" strokeWidth="2" strokeLinejoin="round" data-id="element-489" />
                  <circle cx="32" cy="24" r="4" fill="#D6B25E" data-id="element-490" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold font-serif text-navy mb-4" data-id="element-491">
                Monitor
              </h3>
              <p className="text-slate leading-[1.75]" data-id="element-492">
                Daily FICO 8 monitoring across all three major consumer
                reporting bureaus.
              </p>
            </div>
          </motion.div>

          {/* Document Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} whileHover={{
          y: -8,
          boxShadow: '0 0 30px rgba(31, 169, 113, 0.15)'
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-white p-10 rounded-3xl border border-slate-100 shadow-card relative overflow-hidden group" data-id="element-493">
            <div className="absolute top-0 right-0 w-48 h-48 bg-radial-gradient from-emerald-50 to-transparent opacity-50 pointer-events-none transition-opacity group-hover:opacity-100" data-id="element-494"></div>

            <div className="relative z-10" data-id="element-495">
              <div className="w-16 h-16 mb-8" data-id="element-496">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-497">
                  <rect x="16" y="12" width="32" height="40" rx="2" fill="#1FA971" fillOpacity="0.1" stroke="#0F2747" strokeWidth="2" data-id="element-498" />
                  <path d="M24 24H40" stroke="#0F2747" strokeWidth="2" strokeLinecap="round" data-id="element-499" />
                  <path d="M24 32H40" stroke="#0F2747" strokeWidth="2" strokeLinecap="round" data-id="element-500" />
                  <path d="M24 40H32" stroke="#0F2747" strokeWidth="2" strokeLinecap="round" data-id="element-501" />
                  <rect x="36" y="36" width="12" height="12" rx="6" fill="#D6B25E" stroke="#0F2747" strokeWidth="2" data-id="element-502" />
                  <path d="M40 42L44 46" stroke="#0F2747" strokeWidth="2" strokeLinecap="round" data-id="element-503" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold font-serif text-navy mb-4" data-id="element-504">
                Document
              </h3>
              <p className="text-slate leading-[1.75]" data-id="element-505">
                AI-assisted drafting tools reviewed and submitted directly by
                the consumer.
              </p>
            </div>
          </motion.div>

          {/* Watch Card */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} whileHover={{
          y: -8,
          boxShadow: '0 0 30px rgba(31, 169, 113, 0.15)'
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} className="bg-white p-10 rounded-3xl border border-slate-100 shadow-card relative overflow-hidden group" data-id="element-506">
            <div className="absolute top-0 right-0 w-48 h-48 bg-radial-gradient from-emerald-50 to-transparent opacity-50 pointer-events-none transition-opacity group-hover:opacity-100" data-id="element-507"></div>

            <div className="relative z-10" data-id="element-508">
              <div className="w-16 h-16 mb-8" data-id="element-509">
                <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-510">
                  <path d="M12 32C12 32 20 16 32 16C44 16 52 32 52 32C52 32 44 48 32 48C20 48 12 32 12 32Z" fill="#1FA971" fillOpacity="0.1" stroke="#0F2747" strokeWidth="2" strokeLinejoin="round" data-id="element-511" />
                  <circle cx="32" cy="32" r="8" fill="#1FA971" fillOpacity="0.3" stroke="#0F2747" strokeWidth="2" data-id="element-512" />
                  <circle cx="32" cy="32" r="3" fill="#D6B25E" data-id="element-513" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold font-serif text-navy mb-4" data-id="element-514">
                Watch
              </h3>
              <p className="text-slate leading-[1.75]" data-id="element-515">
                Track bureau updates, investigations, and reporting changes from
                one centralized timeline.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Comparison Table */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-12" data-id="element-516">
          <div className="overflow-x-auto" data-id="element-517">
            <table className="w-full text-left border-collapse min-w-[800px]" data-id="element-518">
              <thead data-id="element-519">
                <tr className="border-b border-slate-200" data-id="element-520">
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs" data-id="element-521">
                    Feature
                  </th>
                  <th className="py-5 px-6 font-bold text-emerald-800 bg-emerald-50 border-l-4 border-emerald-600 w-1/4 uppercase tracking-wide text-xs relative" data-id="element-522">
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap" data-id="element-523">
                      RECOMMENDED
                    </span>
                    ProCreditWatch ($59.99/mo)
                  </th>
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs border-l border-slate-100" data-id="element-524">
                    Outsourced Credit Repair Services
                  </th>
                  <th className="py-5 px-6 font-bold text-charcoal bg-white w-1/4 uppercase tracking-wide text-xs border-l border-slate-100" data-id="element-525">
                    Free Monitoring ($0)
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm" data-id="element-526">
                <tr className="border-b border-slate-100" data-id="element-527">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-528">
                    Monthly Cost
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-529">
                    Flat $59.99 subscription
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-530">
                    High recurring retainers
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-531">
                    Free
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-532">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-533">
                    What You SEE
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-534">
                    Daily bureau visibility
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-535">
                    Limited internal visibility
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-536">
                    Basic score estimates
                  </td>
                </tr>
                <tr className="border-b border-slate-100" data-id="element-537">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-538">
                    What They DO
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-539">
                    Consumer-directed workflows
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-540">
                    Outsourced dispute activity
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-541">
                    Monitoring only
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-542">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-543">
                    Who Controls It
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-544">
                    Consumer reviews every submission
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-545">
                    Third-party operators
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-546">
                    Platform alerts only
                  </td>
                </tr>
                <tr className="border-b border-slate-100" data-id="element-547">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-548">
                    Time to Visibility
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-549">
                    Daily updates
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-550">
                    Often unclear
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-551">
                    Delayed or generalized
                  </td>
                </tr>
                <tr className="border-b border-slate-100 bg-slate-50" data-id="element-552">
                  <td className="py-4 px-6 font-semibold text-navy" data-id="element-553">
                    Transparency
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50/80 border-l-4 border-emerald-600" data-id="element-554">
                    Centralized activity history
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-555">
                    Varies by provider
                  </td>
                  <td className="py-4 px-6 text-slate border-l border-slate-100" data-id="element-556">
                    Limited reporting detail
                  </td>
                </tr>
                <tr data-id="element-557">
                  <td className="py-4 px-6 font-semibold text-navy bg-white" data-id="element-558">
                    Action Layer
                  </td>
                  <td className="py-4 px-6 text-slate bg-emerald-50 border-l-4 border-emerald-600" data-id="element-559">
                    Drafting + monitoring infrastructure
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-560">
                    Outsourced process
                  </td>
                  <td className="py-4 px-6 text-slate bg-white border-l border-slate-100" data-id="element-561">
                    No structured workflow
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>

        <div className="text-center" data-id="element-562">
          <a href="https://procreditwatch.com/redirect.asp?guid=ZMEA756999SU" target="_blank" rel="noopener sponsored" data-product="procreditwatch" onClick={() => trackAffiliateClick('procreditwatch', 'ProCreditWatch')} className="inline-flex items-center justify-center bg-emerald hover:bg-emerald-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-200 shadow-sm hover:shadow-md" data-id="element-563">
            View Monitoring Infrastructure
            <ExternalLink className="ml-2 w-5 h-5" data-id="element-564" />
          </a>
          <p className="mt-4 text-sm text-slate-500 italic" data-id="element-565">
            No score guarantees. Consumers review and submit all disputes
            independently.
          </p>
        </div>
      </div>
    </section>;
}